import { inject, Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {APIRequestResources, CachedAPIRequest, LoadingService, PaginationResponse} from "../../../core";
import {Router} from "@angular/router";
import { BehaviorSubject, catchError, finalize, Subject, tap } from 'rxjs';
import {GetJobOperationDTO, JobDTO, JobOperationItemDTO, MachineRescheduleDTO, RescheduleJobOperationDTO, RescheduledHoldReasonDTO} from '../interface/jop-operation.entity';
import { toSignal } from '@angular/core/rxjs-interop';
import { handleError } from 'src/app/core/utils/utils';


@Injectable({
  providedIn: 'root'
})
export class JobOperationService extends CachedAPIRequest {

      loading = inject(LoadingService);
      private refreshTrigger = new Subject<void>();
  
      $all = new BehaviorSubject<JobDTO[]>([]);
      all = toSignal(this.$all, {initialValue: []});

      $allOp = new BehaviorSubject<JobOperationItemDTO[]>([]);
      allOp = toSignal(this.$allOp, {initialValue: []});
  
      $active = new BehaviorSubject<any | undefined>(undefined);
      active = toSignal(this.$active, {initialValue: undefined});
  
      private readonly $statistics = new BehaviorSubject<any>(undefined)
      stat = toSignal(this.$statistics, {initialValue: undefined})

  constructor(protected override http: HttpClient, private router: Router) {
    super(http, APIRequestResources.JobOperationService)
  }

  create = (jobOperation: any) => {
    return this.post<any>(jobOperation, {}).pipe(
    );
}

getById = (id: string, refresh = true) => {
    return this.get<GetJobOperationDTO>({id}, refresh ? 'freshness' : 'performance')
        .pipe(tap((res) => this.$active.next(res.data)),);
}

find = (searchParams: any, refresh = true) => {
        this.loading.set(true, { immediate: true });
        return this.get<PaginationResponse<JobDTO[]>>(
            {endpoint: `find`, params: searchParams,},
            refresh ? 'freshness' : 'performance'
        ).pipe(
            tap((res) => {
                this.$all.next(res.data.data);
            }),
            tap((res) => {
                const {data, ...obj} = res.data;
                this.$statistics.next(obj);
            }),
            finalize(() => {
                this.loading.set(false);
            })
        );
    };

    deliveryFind = (departmentId: number, refresh = true) => {
        const endpoint = `delivery-find?department=${departmentId}`;
        return this.get<JobDTO[]>({ endpoint }, refresh ? 'freshness' : 'performance')
            .pipe(
                tap(res => this.$all.next(res.data ?? [])),
                catchError(handleError)
            );
    }

    search = (searchParams: any, refresh = true) => {
        this.loading.set(true, { immediate: true });
        return this.get<PaginationResponse<JobOperationItemDTO[]>>(
            {endpoint: `search`, params: searchParams,},
            refresh ? 'freshness' : 'performance'
        ).pipe(
            tap((res) => {
                this.$allOp.next(res.data.data);
            }),
            tap((res) => {
                const {data, ...obj} = res.data;
                this.$statistics.next(obj);
            }),
            finalize(() => {
                this.loading.set(false);
            })
        );
    };

    triggerRefresh() {
        this.refreshTrigger.next();
    }

    getRefreshObservable() {
        return this.refreshTrigger.asObservable();
    }

    updateJobOperation = (id: number, rescheduleJobOperationDTO: RescheduleJobOperationDTO) => {
        return this.put<any>(rescheduleJobOperationDTO, {endpoint: `reschedule-existing-operations/${id}`
        }).pipe(

        )
    }

    addMaintenance = (maintenanceData: any) => {
        return this.post<any>(maintenanceData, { endpoint: 'maintenance' }).pipe(
        );
    }

    cancelMaintenance = (id: number, payload: { remarks: string }) => {
        return this.patch<any>(payload, { endpoint: `maintenance/cancel/${id}` }).pipe(
        );
    }

    machineReschedule = (id: number, dto: MachineRescheduleDTO) => {
        return this.put<any>(dto, { endpoint: `machine-reschedule-existing-operations/${id}` }).pipe(
        );
    }

    getRescheduledHoldReason = (id: number, refresh = true) => {
        return this.get<RescheduledHoldReasonDTO>({ endpoint: `rescheduled-hold-reason/${id}` }, refresh ? 'freshness' : 'performance').pipe(
        );
    }

    removeGaps = (id: number, newEstimatedDateTime: string) => {
        return this.patch<any>({}, { 
            endpoint: `remove-gaps/${id}`,
            params: { new_estimated_date_time: newEstimatedDateTime }
        }).pipe();
    }

    getByJobCardId = (jobCardId: number | string, workOrderId?: string, refresh = true) => {
        const params: any = {};
        if (workOrderId) {
            params.workOrderId = workOrderId;
        }
        return this.get<any>({
            endpoint: `job-card/${jobCardId}`,
            params
        }, refresh ? 'freshness' : 'performance');
    }

    updateIsFinal = (id: number | string, isFinalOrPayload: boolean | { isFinal: boolean }) => {
        const payload = typeof isFinalOrPayload === 'boolean' ? { isFinal: isFinalOrPayload } : isFinalOrPayload;
        return this.patch<any>(payload, { endpoint: `${id}/is-final` }).pipe();
    }

}
